import React, { useEffect } from 'react';

// React 19 natively hoists <meta>/<link> rendered in components into <head>.
// (react-helmet-async is incompatible with React 19 and silently no-ops.)

// Helper to generate URL-friendly slugs
const generateSlug = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .substring(0, 60);
};

const SEO = ({ 
  title, 
  description, 
  image, 
  url, 
  type = 'website',
  product = null,
  category = null,
  breadcrumbs = null,
  seller = null,
  noindex = false
}) => {
  const baseUrl = 'https://mintedbuysell.com.au';
  const defaultImage = 'https://mintedbuysell.com.au/icons/icon-512x512.png';
  
  const fullTitle = title ? `${title} | Minted` : 'Minted - Buy & Sell Near You';
  const fullUrl = url ? `${baseUrl}${url}` : baseUrl;
  const finalImage = image || defaultImage;
  const finalDescription = description || "The marketplace for buying and selling near you. Find great deals on clothing, electronics, toys, furniture & more.";

  // Generate breadcrumb structured data
  const getBreadcrumbSchema = () => {
    const items = [
      { name: 'Home', url: baseUrl }
    ];
    
    if (category) {
      items.push({ name: category.name, url: `${baseUrl}/category/${category.id}` });
    }
    
    if (product) {
      if (product.category) {
        items.push({ name: product.category, url: `${baseUrl}/category/${product.category}` });
      }
      items.push({ name: product.title, url: `${baseUrl}/product/${product.id}` });
    }
    
    if (breadcrumbs) {
      breadcrumbs.forEach(b => items.push({ name: b.name, url: `${baseUrl}${b.url}` }));
    }
    
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.url
      }))
    };
  };

  // Enhanced product schema
  const getProductSchema = () => {
    if (!product) return null;
    
    const schema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.title,
      "description": product.description,
      "image": product.images || [],
      "sku": product.id,
      "mpn": product.id,
      "brand": {
        "@type": "Brand",
        "name": "Preloved"
      },
      "offers": {
        "@type": "Offer",
        "url": `${baseUrl}/product/${product.id}`,
        "price": product.price,
        "priceCurrency": "AUD",
        "availability": product.sold 
          ? "https://schema.org/SoldOut" 
          : "https://schema.org/InStock",
        "itemCondition": product.condition === 'New' 
          ? "https://schema.org/NewCondition" 
          : "https://schema.org/UsedCondition",
        "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        "shippingDetails": product.shippingAvailable ? {
          "@type": "OfferShippingDetails",
          "shippingRate": {
            "@type": "MonetaryAmount",
            "value": product.shippingCost || 0,
            "currency": "AUD"
          },
          "shippingDestination": {
            "@type": "DefinedRegion",
            "addressCountry": "AU"
          }
        } : undefined
      },
      "category": product.category,
      "additionalProperty": [
        {
          "@type": "PropertyValue",
          "name": "Condition",
          "value": product.condition
        },
        {
          "@type": "PropertyValue",
          "name": "Location",
          "value": product.location
        }
      ]
    };
    
    // Add seller info if available
    if (seller || product.seller_name) {
      schema.offers.seller = {
        "@type": "Person",
        "name": seller?.name || product.seller_name || "Minted Seller"
      };
    }
    
    return schema;
  };

  // Local business schema for homepage
  const getLocalBusinessSchema = () => {
    if (type !== 'website' || url !== '/') return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Minted Buy & Sell",
      "description": "The marketplace for buying and selling preloved items",
      "url": baseUrl,
      "logo": `${baseUrl}/icons/icon-512x512.png`,
      "image": defaultImage,
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -33.8688,
        "longitude": 151.2093
      },
      "areaServed": {
        "@type": "Country",
        "name": "Australia"
      },
      "priceRange": "$"
    };
  };

  // WebSite schema with sitelinks search box + Organization (homepage only)
  const getWebsiteSchemas = () => ([
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Minted",
      "url": baseUrl,
      "potentialAction": {
        "@type": "SearchAction",
        "target": { "@type": "EntryPoint", "urlTemplate": `${baseUrl}/search?q={search_term_string}` },
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Minted",
      "url": baseUrl,
      "logo": `${baseUrl}/icons/icon-512x512.png`,
      "sameAs": ["https://www.facebook.com/mintedbuysell"]
    }
  ]);

  return (
    <>
      {/* Primary Meta Tags */}
      <TitleUpdater title={fullTitle} />
      <meta name="title" content={fullTitle} />
      <meta name="description" content={finalDescription} />
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === 'product' ? 'product' : 'website'} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:site_name" content="Minted" />
      <meta property="og:locale" content="en_AU" />
      
      {/* Product-specific OG tags */}
      {product && (
        <>
          <meta property="product:price:amount" content={product.price} />
          <meta property="product:price:currency" content="AUD" />
          <meta property="product:availability" content={product.sold ? 'out of stock' : 'in stock'} />
          <meta property="product:condition" content={product.condition === 'New' ? 'new' : 'used'} />
        </>
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Breadcrumb structured data */}
      {(product || category || breadcrumbs) && (
        <script type="application/ld+json">
          {JSON.stringify(getBreadcrumbSchema())}
        </script>
      )}
      
      {/* Product structured data */}
      {product && (
        <script type="application/ld+json">
          {JSON.stringify(getProductSchema())}
        </script>
      )}
      
      {/* Local business schema for homepage */}
      {type === 'website' && !product && (
        <script type="application/ld+json">
          {JSON.stringify(getLocalBusinessSchema())}
        </script>
      )}

      {/* WebSite + Organization schema (homepage) */}
      {url === '/' && (
        <script type="application/ld+json">
          {JSON.stringify(getWebsiteSchemas())}
        </script>
      )}
    </>
  );
};

const TitleUpdater = ({ title }) => {
  useEffect(() => {
    document.title = title;
  }, [title]);
  return null;
};

export default SEO;
